import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking with session replay
 * Configured specifically for dashboard error tracking
 * Can be enabled in development for testing by setting VITE_ENABLE_SENTRY_LOCAL=true
 */
export const initSentry = () => {
  // Initialize in production OR if explicitly enabled for local testing
  const isProduction =
    import.meta.env.MODE === "production" || import.meta.env.PROD;
  const enableLocal = import.meta.env.VITE_ENABLE_SENTRY_LOCAL === "true";

  if (isProduction || enableLocal) {
    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) {
      console.warn("Sentry DSN not found. Error tracking will not be enabled.");
      return;
    }

    if (enableLocal && !isProduction) {
      console.log("🔍 Sentry enabled for local testing");
    }

    Sentry.init({
      dsn,
      environment: isProduction ? "production" : "development",
      integrations: [
        Sentry.replayIntegration({
          // Session Replay configuration
          maskAllText: true, // Mask all text for privacy
          blockAllMedia: true, // Block all media for privacy
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of transactions for dashboard debugging

      // Session Replay sampling
      replaysSessionSampleRate: 0, // Don't record all sessions
      replaysOnErrorSampleRate: 1.0, // Record 100% of sessions with errors

      // Release tracking - must match the release name used in build script
      // Format: baseVersion-YYYYMMDD-HHMMSS (auto-generated in build.sh)
      release: import.meta.env.VITE_APP_VERSION || undefined,

      // Source Maps Configuration
      // This tells Sentry to use source maps for readable stack traces
      // Source maps should be uploaded to Sentry or served with your app
      // For now, we'll rely on browser source maps if they're accessible
      // For better results, upload source maps to Sentry (see SENTRY_SOURCE_MAPS.md)
      normalizeDepth: 10, // Increase depth for better stack traces

      // Filter out non-dashboard errors if needed
      beforeSend(event, hint) {
        // Only send errors that occur on dashboard routes
        const url = event.request?.url || window.location.href;
        const isDashboardRoute =
          url.includes("/dashboard") ||
          url === window.location.origin + "/" ||
          (url === window.location.origin &&
            !url.includes("/login") &&
            !url.includes("/register"));

        // For dashboard-specific tracking, you can filter here
        // For now, we'll track all errors but tag dashboard ones
        if (isDashboardRoute) {
          event.tags = {
            ...event.tags,
            route: "dashboard",
            component: "Dashboard",
          };
        }

        return event;
      },
    });
  }
};

/**
 * Set user context for Sentry
 */
export const setSentryUser = (user: {
  id: string | number;
  email?: string;
  name?: string;
  companyId?: string | number;
  role?: string;
}) => {
  const isProduction =
    import.meta.env.MODE === "production" || import.meta.env.PROD;
  const enableLocal = import.meta.env.VITE_ENABLE_SENTRY_LOCAL === "true";

  if (isProduction || enableLocal) {
    Sentry.setUser({
      id: user.id.toString(),
      email: user.email,
      username: user.name,
      companyId: user.companyId?.toString(),
      role: user.role,
    });
  }
};

/**
 * Clear user context
 */
export const clearSentryUser = () => {
  const isProduction =
    import.meta.env.MODE === "production" || import.meta.env.PROD;
  const enableLocal = import.meta.env.VITE_ENABLE_SENTRY_LOCAL === "true";

  if (isProduction || enableLocal) {
    Sentry.setUser(null);
  }
};

/**
 * Capture exception manually
 */
export const captureException = (
  error: Error,
  context?: Record<string, any>
) => {
  const isProduction =
    import.meta.env.MODE === "production" || import.meta.env.PROD;
  const enableLocal = import.meta.env.VITE_ENABLE_SENTRY_LOCAL === "true";

  if (isProduction || enableLocal) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext("additional", context);
      }
      scope.setTag("source", "dashboard");
      Sentry.captureException(error);
    });
  } else {
    console.error("Error (not sent to Sentry in dev):", error, context);
  }
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (
  message: string,
  category?: string,
  level?: Sentry.SeverityLevel
) => {
  const isProduction =
    import.meta.env.MODE === "production" || import.meta.env.PROD;
  const enableLocal = import.meta.env.VITE_ENABLE_SENTRY_LOCAL === "true";

  if (isProduction || enableLocal) {
    Sentry.addBreadcrumb({
      message,
      category: category || "default",
      level: level || "info",
    });
  }
};
